import React, { useState, useEffect } from 'react';
import { History, Save, RotateCcw, Plus, Trash2, Calendar, ShieldCheck } from 'lucide-react';
import { CustomProject } from '../../types/productBuilder';
import { ProjectVersion } from '../types';

interface VersionsProps {
  project: CustomProject;
  onUpdateProject: (p: CustomProject) => void;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

export const Versions: React.FC<VersionsProps> = ({ project, onUpdateProject, onAddLog }) => {
  const [versionList, setVersionList] = useState<ProjectVersion[]>([]);
  const [newLabel, setNewLabel] = useState('');

  // Key-specific list for this project's versions history
  const loadVersions = () => {
    const stored = localStorage.getItem(`versions_hist_${project.id}`);
    if (stored) {
      setVersionList(JSON.parse(stored));
    } else {
      // Seed initial version snapshot
      const seeds: ProjectVersion[] = [
        {
          id: `ver_init_${Date.now()}`,
          name: 'Initial Scaffold (Automatic)',
          timestamp: new Date(Date.now() - 3600 * 1000).toISOString(),
          projectData: JSON.stringify(project)
        }
      ];
      localStorage.setItem(`versions_hist_${project.id}`, JSON.stringify(seeds));
      setVersionList(seeds);
    }
  };

  useEffect(() => {
    loadVersions();
  }, [project.id]);

  const handleSnapshot = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLabel.trim()) return;

    const newVer: ProjectVersion = {
      id: `ver_${Date.now()}`,
      name: newLabel.trim().toUpperCase(),
      timestamp: new Date().toISOString(),
      projectData: JSON.stringify(project)
    };

    const updatedList = [newVer, ...versionList];
    setVersionList(updatedList);
    localStorage.setItem(`versions_hist_${project.id}`, JSON.stringify(updatedList));
    setNewLabel('');
    onAddLog('Product Builder', `Created version snapshot "${newVer.name}"`, 'SUCCESS', 'Custom project state serialised and stored.');
    alert(`SNAPSHOT TAKEN!\n\nVersion "${newVer.name}" captured successfully in sandbox database.`);
  };

  const handleRollback = (ver: ProjectVersion) => {
    const doubleCheck = window.confirm(`Restore active workspace project state to "${ver.name}" snapshots? All unsaved current configurations will be overridden.`);
    if (!doubleCheck) return;

    const parsed: CustomProject = JSON.parse(ver.projectData);
    onUpdateProject({
      ...parsed,
      id: project.id // Maintain project_id stability
    });

    onAddLog('Product Builder', `Rolled back workspace to snapshot "${ver.name}"`, 'SUCCESS', 'Deserialised project parameters replaced.');
    alert(`ROLLBACK PROGRESS COMPLETE!\n\nPrerelease sandbox reverted to: "${ver.name}". Update active viewport preview.`);
  };

  const removeSnapshot = (id: string, name: string) => {
    const updated = versionList.filter(v => v.id !== id);
    setVersionList(updated);
    localStorage.setItem(`versions_hist_${project.id}`, JSON.stringify(updated));
    onAddLog('Product Builder', `Purged history revision "${name}"`, 'SUCCESS', 'Cleaned database nodes.');
  };

  return (
    <div className="npx-builder-versions-container space-y-6 text-left animate-fade-in">
      <div className="border-b border-white/5 pb-4">
        <div className="flex items-center gap-1.5">
          <History className="w-4 h-4 text-[#00ffff]" />
          <h2 className="text-sm font-mono tracking-widest text-[#00ffff] uppercase font-black">9. Incremental History (Versions)</h2>
        </div>
        <p className="text-xs text-zinc-400 mt-1">Capture state snapshots of your preview layout, enabling immediate, seamless rollback comparison without server compilation wait times.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
        
        {/* SNAPSHOT FORM (Col 5) */}
        <div className="md:col-span-4 bg-black/20 p-5 rounded-2xl border border-white/5 h-full flex flex-col justify-between">
          <form onSubmit={handleSnapshot} className="space-y-4">
            <h3 className="text-xs font-bold font-mono text-white uppercase tracking-wider flex items-center gap-2">
              <Save className="w-4 h-4 text-purple-400" />
              <span>Snapshot State</span>
            </h3>

            <div className="space-y-1.5 text-left">
              <label className="text-[10px] font-mono text-zinc-400 uppercase font-bold">Revision Marker Name</label>
              <input
                type="text"
                required
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="e.g. Revision V2 - Cyan"
                className="w-full bg-black/40 border border-zinc-900 rounded-lg p-2 text-xs text-white focus:outline-none focus:border-[#00ffff] font-mono uppercase"
              />
            </div>

            <button
              type="submit"
              className="w-full font-mono text-[9.5px] font-black uppercase text-[#020202] hover:text-white bg-gradient-to-r from-[#00ffff] to-purple-500 py-2.5 rounded-xl text-center cursor-pointer transition-colors border border-cyan-500/10 flex items-center justify-center gap-1.5"
            >
              <Plus size={11} className="text-current" />
              <span>Snapshot Current Design</span>
            </button>
          </form>
        </div>

        {/* VERSIONS TIMELINE (Col 8) */}
        <div className="md:col-span-8 bg-black/20 p-5 rounded-2xl border border-white/5 space-y-3">
          <h3 className="text-xs font-bold font-mono text-white uppercase tracking-wider">History Timestamps List</h3>
          
          <div className="space-y-2 max-h-[220px] overflow-y-auto custom-scrollbar">
            {versionList.map((ver) => (
              <div
                key={ver.id}
                className="p-3.5 bg-black/30 border border-zinc-900 rounded-xl flex items-center justify-between text-left font-mono"
              >
                <div className="space-y-1 leading-none text-left">
                  <span className="text-[10.5px] font-bold uppercase text-[#00ffff] block tracking-wider">⏱ {ver.name}</span>
                  <span className="text-[8px] text-zinc-500 flex items-center gap-1">
                    <Calendar size={10} />
                    <span>{new Date(ver.timestamp).toLocaleString()}</span>
                  </span>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleRollback(ver)}
                    className="px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-white/5 rounded-lg text-[8.5px] text-zinc-305 font-bold uppercase flex items-center gap-1 cursor-pointer"
                  >
                    <RotateCcw size={10} />
                    <span>Rollback</span>
                  </button>
                  {versionList.length > 1 && (
                    <button
                      onClick={() => removeSnapshot(ver.id, ver.name)}
                      className="p-1.5 hover:text-red-400 text-zinc-600 transition-colors"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      <div className="p-4 bg-zinc-950/40 border border-white/5 rounded-2xl flex items-start gap-3">
        <ShieldCheck size={15} className="text-[#00ff00] shrink-0 mt-0.5" />
        <div className="space-y-1">
          <h4 className="text-[11px] font-mono text-white uppercase font-bold">Local snapshot encryption protocols</h4>
          <p className="text-[9.5px] text-zinc-500 leading-relaxed font-sans normal-case">
            All captured snapshots are serialised to string caches and contained specifically within your browser session details. It enforces rigid local security guidelines, ensuring corporate client data isolates safely under any multi-user environments.
          </p>
        </div>
      </div>
    </div>
  );
};
